import type { GameState } from 'tower/state/game-state';
import { Flags } from './types';

export const DEPTHS = {
  Fade: 100,
  Menu: 20,
  Ceiling: 12,
  Projectiles: 11,
  Player: 10,
  Sword: 9,
  Enemies: 8,
  Items: 7,
  Doorway: 6,
  Walls: 5,
  Floor: 0,
};

export const DEFAULT_VOLUME = 0.48;
export const VOLUME_LEVELS = [0, 0.12, 0.24, 0.36, 0.48, 0.6, 0.72, 0.84, 1];

export const GAME_WIDTH = 256;
export const GAME_HEIGHT = 256;
export const PLAYER_VELOCITY = 80;
export const ROOM_WIDTH = 416;
export const ROOM_HEIGHT = 224;
export const CAMERA_VIEWPORT_WIDTH = 256;
export const CAMERA_VIEWPORT_HEIGHT = 224;
export const MAZE_WIDTH = 16;
export const MAZE_HEIGHT = 8;
export const FLOOR_WIDTH_ROOMS = 8;
export const FLOOR_HEIGHT_ROOMS = 9;

export const HURT_MUSIC_THRESHOLD = 3;

export const ENEMY_PUSH_SPEED = 250;
export const ENEMY_PUSH_FRAMES = 6;
export const PLAYER_PUSH_SPEED = 250;
export const PLAYER_PUSH_FRAMES = 6;

export const KNIGHT_SHIELD_CONE_SIZE = 90;
export const HEART_DROP_CHANCE = 0.33;

export const BOOMERANG_THROW_VELOCITY = 220;
export const BOOMERANG_THROW_ACC = 300;

export const FIREBALL_SPEED = 110;

export const GAMESTORAGE_KEY = 'dev_gameStorage';
export const SHAREDSTORAGE_KEY = 'dev_sharedStorage';

export const CREDITS_STOP_THRESHOLD = 10;
export const CREDITS_MIN_SPEED = 20;
export const CREDITS_MAX_SPEED = 100;
export const CREDITS_ACC = 200;

interface Item {
  key: string;
  itemFrame: number;
  getInventoryFrame(gameState: GameState): number;
  getSubweaponFrame?: (gameState: GameState) => number;
  isSubweapon: boolean;
  longCollectAnim: boolean;
  animation?: string;
  bodySize?: { width: number; height: number };
}

export const ITEMS: Record<string, Item> = {
  sword: {
    key: 'sword',
    itemFrame: 1,
    getInventoryFrame: () => 1,
    getSubweaponFrame: () => 1,
    isSubweapon: false,
    longCollectAnim: true,
  },
  whiteSword: {
    key: 'whiteSword',
    itemFrame: 2,
    getInventoryFrame: () => 2,
    getSubweaponFrame: () => 2,
    isSubweapon: false,
    longCollectAnim: true,
  },
  masterSword: {
    key: 'masterSword',
    itemFrame: 9,
    getInventoryFrame: () => 9,
    getSubweaponFrame: () => 9,
    isSubweapon: false,
    longCollectAnim: true,
  },
  key: {
    key: 'key',
    itemFrame: 8,
    getInventoryFrame: () => 8,
    isSubweapon: false,
    longCollectAnim: false,
  },
  smallHeart: {
    key: 'smallHeart',
    itemFrame: 57,
    getInventoryFrame: () => 57,
    isSubweapon: false,
    longCollectAnim: false,
    animation: 'smallHeart',
    bodySize: { width: 8, height: 8 },
  },
  heart1: {
    key: 'heart',
    itemFrame: 33,
    getInventoryFrame: () => 33,
    isSubweapon: false,
    longCollectAnim: true,
  },
  heart2: {
    key: 'heart',
    itemFrame: 33,
    getInventoryFrame: () => 33,
    isSubweapon: false,
    longCollectAnim: true,
  },
  heart3: {
    key: 'heart',
    itemFrame: 33,
    getInventoryFrame: () => 33,
    isSubweapon: false,
    longCollectAnim: true,
  },
  mattock: {
    key: 'mattock',
    itemFrame: 3,
    isSubweapon: true,
    longCollectAnim: true,

    getSubweaponFrame(gameState) {
      return this.getInventoryFrame(gameState);
    },

    getInventoryFrame(gameState) {
      if (gameState.mattockDurability >= 1) {
        return 3;
      }

      return 4;
    },
  },
  betterMattock: {
    key: 'betterMattock',
    itemFrame: 5,
    isSubweapon: true,
    longCollectAnim: true,

    getSubweaponFrame(gameState) {
      return this.getInventoryFrame(gameState);
    },

    getInventoryFrame(gameState) {
      if (gameState.mattockDurability >= 1) {
        return 5;
      }

      return 6;
    },
  },

  bestMattock: {
    key: 'bestMattock',
    itemFrame: 7,
    isSubweapon: true,
    longCollectAnim: true,

    getSubweaponFrame(gameState) {
      return this.getInventoryFrame(gameState);
    },

    getInventoryFrame() {
      return 7;
    },
  },

  boomerang: {
    key: 'boomerang',
    itemFrame: 24,
    getInventoryFrame: () => 24,
    getSubweaponFrame: () => 24,
    isSubweapon: true,
    longCollectAnim: true,
  },
  candle: {
    key: 'candle',
    itemFrame: 34,
    isSubweapon: true,
    longCollectAnim: true,

    getSubweaponFrame(gameState) {
      return this.getInventoryFrame(gameState);
    },

    getInventoryFrame(gameState) {
      switch (gameState.candleDurability) {
        case 3:
          return 34;
        case 2:
          return 35;
        case 1:
          return 36;
        default:
          return 37;
      }
    },
  },
  bookOfTruth: {
    key: 'bookOfTruth',
    itemFrame: 41,
    getInventoryFrame: () => 41,
    isSubweapon: false,
    longCollectAnim: true,
  },
  wispRing: {
    key: 'wispRing',
    itemFrame: 42,
    getInventoryFrame: () => 42,
    isSubweapon: false,
    longCollectAnim: true,
  },
  bow: {
    key: 'bow',
    itemFrame: 45,
    getInventoryFrame: () => 46,
    getSubweaponFrame: () => 47,
    isSubweapon: true,
    longCollectAnim: true,
  },
  magicKey: {
    key: 'magicKey',
    itemFrame: 48,
    getInventoryFrame: () => 48,
    isSubweapon: false,
    longCollectAnim: true,
  },
  blueTunic: {
    key: 'blueTunic',
    itemFrame: 44,
    getInventoryFrame: () => 44,
    isSubweapon: false,
    longCollectAnim: true,
  },
  betterShield: {
    key: 'betterShield',
    itemFrame: 49,
    getInventoryFrame: () => 49,
    isSubweapon: false,
    longCollectAnim: true,
  },
  potion: {
    key: 'potion',
    itemFrame: 50,
    getInventoryFrame: () => 50,
    getSubweaponFrame: () => 50,
    isSubweapon: true,
    longCollectAnim: true,
  },
  blueRod: {
    key: 'blueRod',
    itemFrame: 51,
    getInventoryFrame: () => 51,
    isSubweapon: false,
    longCollectAnim: true,
  },
  redRod: {
    key: 'redRod',
    itemFrame: 52,
    getInventoryFrame: () => 52,
    isSubweapon: false,
    longCollectAnim: true,
  },
};

export type ItemKey = keyof typeof ITEMS;

export const INVENTORY_DISPLAY_ITEMS: ItemKey[] = [
  'mattock',
  'betterMattock',
  'bestMattock',
  'boomerang',
  'candle',
  'bow',
  'potion',
  'blueTunic',
  'bookOfTruth',
  'wispRing',
  'betterShield',
  'magicKey',
  'blueRod',
  'redRod',
];

export const SWORD_VARIANTS = {
  sword: {
    frame: 0,
    damage: 1,
  },
  whiteSword: {
    frame: 2,
    damage: 2,
  },
  masterSword: {
    frame: 4,
    damage: 12,
  },
};

export interface GameStateOverride {
  items?: string[];
  hp?: number;
  subweapon?: string;
  checkpointRoomKey?: string;
  flags?: Flags;
}

export const OVERRIDES: Record<string, GameStateOverride> = {
  everything: {
    items: [
      'masterSword',
      'boomerang',
      'candle',
      'bow',
      'bestMattock',
      'bookOfTruth',
      'magicKey',
      'blueTunic',
      'heart1',
      'heart2',
      'heart3',
      'wispRing',
      'betterShield',
      'redRod',
      'blueRod',
    ],
    subweapon: 'bestMattock',
    hp: 13,
    checkpointRoomKey: 'start',
    flags: { disableFlashing: false, redRodPlaced: false, blueRodPlaced: false, startedGame: true },
  },
  end: {
    items: [
      'masterSword',
      'boomerang',
      'candle',
      'bow',
      'bestMattock',
      'bookOfTruth',
      'magicKey',
      'blueTunic',
      'heart1',
      'heart2',
      'heart3',
      'wispRing',
      'betterShield',
      'redRod',
      'blueRod',
    ],
    subweapon: 'bestMattock',
    hp: 13,
    checkpointRoomKey: 'lastroom',
    flags: { disableFlashing: false, redRodPlaced: true, blueRodPlaced: true, startedGame: true },
  },
};
